import { useState, useEffect, useMemo, useRef } from 'react';
import { apiService } from '../services/apiService';
import { durationFromEndTime, endTimeInputValue } from '../utils/eventHelpers';
import { showToast } from '../components/Toast';
import { parseErrorMessage } from '../utils/formErrors';
import { websocketService } from '../services/websocketService';
import { canPurchasePingolfCards as deriveCanPurchasePingolfCards } from '../utils/pingolfLifecycle';


// Owns all data-fetching, websocket subscriptions, and mutation handlers for
// the EventDetails page (core event/RSVP/host data plus the PinGolf card and
// leaderboard subsystem). JSX-only derived booleans (e.g. isPingolfEvent,
// canManageFeaturedGame) stay in the page component since they're purely
// presentational and read directly off state this hook already returns.
export function useEventDetails(eventId, currentUser, isAdmin, navigate) {
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState(null);
  const [showHostAssignment, setShowHostAssignment] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showWalkinForm, setShowWalkinForm] = useState(false);
  const [walkinData, setWalkinData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [editEventData, setEditEventData] = useState({
    title: '',
    description: '',
    event_date: '',
    location: '',
    event_category: 'other',
    event_metadata: {}
  });
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [sendingUpdate, setSendingUpdate] = useState(false);
  const [showMyRsvpForm, setShowMyRsvpForm] = useState(false);
  const [myRsvpData, setMyRsvpData] = useState({ status: 'going', message: '' });
  const [myCurrentRsvp, setMyCurrentRsvp] = useState(null);
  const [eventUpdates, setEventUpdates] = useState([]);
  const [showFeaturedGameForm, setShowFeaturedGameForm] = useState(false);
  const [featuredGameData, setFeaturedGameData] = useState({
    game_name: '',
    game_description: ''
  });
  const [showRsvpActionsMenu, setShowRsvpActionsMenu] = useState(false);
  const [expandedHostBios, setExpandedHostBios] = useState({});
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [activePaymentQr, setActivePaymentQr] = useState(null);
  const [pingolfCards, setPingolfCards] = useState([]);
  const [loadingPingolfCards, setLoadingPingolfCards] = useState(false);
  const [buyingPingolfCard, setBuyingPingolfCard] = useState(false);
  const [abandoningPingolfCardId, setAbandoningPingolfCardId] = useState(null);
  const [purchaseTargetUserId, setPurchaseTargetUserId] = useState('');
  // Everyone a host/admin can buy a card on behalf of. Kept separate from `users` (the
  // admin-only roster used for host assignment): a host taking cash at the door needs this
  // list too, and it comes from an event-scoped endpoint they are allowed to call.
  const [pingolfPurchaseCandidates, setPingolfPurchaseCandidates] = useState([]);
  const [useTabForPingolfPurchase, setUseTabForPingolfPurchase] = useState(true);
  // Whether the signed-in user is allowed to run a tab. Only /users/me exposes this for a
  // non-admin (the admin users list carries it for everyone else), and the purchase modal
  // needs it to decide whether offering "allow tab" is meaningful at all.
  const [currentUserTabEnabled, setCurrentUserTabEnabled] = useState(false);
  const [pingolfPurchaseError, setPingolfPurchaseError] = useState(null);
  const [selectedPingolfCardId, setSelectedPingolfCardId] = useState(null);
  const [savingPingolfResultHoleId, setSavingPingolfResultHoleId] = useState(null);
  const [submittingPingolfCard, setSubmittingPingolfCard] = useState(false);
  const [pingolfDraftHoleInputs, setPingolfDraftHoleInputs] = useState({});
  // Save failures for a single hole (e.g. "finalTotal must be below the target"). Kept apart
  // from the page-level error so the hole entry modal can show it where the user is typing,
  // instead of behind the modal on the card page.
  const [pingolfHoleSaveError, setPingolfHoleSaveError] = useState(null);
  // The hole whose draft input must survive a server refetch - see the sync effect below.
  const preservedHoleInputIdRef = useRef(null);
  const setPreservedPingolfHoleInputId = (holeId) => {
    preservedHoleInputIdRef.current = holeId;
  };
  const [reopeningPingolfCardId, setReopeningPingolfCardId] = useState(null);
  const [correctingPingolfHoleId, setCorrectingPingolfHoleId] = useState(null);
  const [pingolfLeaderboard, setPingolfLeaderboard] = useState([]);
  const [loadingPingolfLeaderboard, setLoadingPingolfLeaderboard] = useState(false);
  const [pingolfLeaderboardError, setPingolfLeaderboardError] = useState(null);
  const [pingolfLeaderboardDetailRow, setPingolfLeaderboardDetailRow] = useState(null);
  const [isPingolfLiveUpdatesPaused, setIsPingolfLiveUpdatesPaused] = useState(false);
  const [showPingolfPurchaseModal, setShowPingolfPurchaseModal] = useState(false);
  const [showPingolfSubmitConfirmModal, setShowPingolfSubmitConfirmModal] = useState(false);
  const [pingolfAbandonTargetCardId, setPingolfAbandonTargetCardId] = useState(null);
  const [pingolfReopenTargetCardId, setPingolfReopenTargetCardId] = useState(null);
  const [pingolfReopenReason, setPingolfReopenReason] = useState('');
  const [pingolfCorrectionDraft, setPingolfCorrectionDraft] = useState({
    cardId: null,
    holeId: null,
    existingReport: null,
    reached: null,
    crossBall: '',
    doubled: false,
    finalTotal: '',
    reason: ''
  });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmDialogSubmitting, setConfirmDialogSubmitting] = useState(false);

  const hostUserOptions = useMemo(() => {
    return users.map(user => ({
      value: user.id,
      label: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim(),
      searchText: `${user.first_name || ''} ${user.last_name || ''} ${user.email || ''} ${user.username || ''}`.toLowerCase()
    }));
  }, [users]);

  // 400+ members, so the picker is a searchable combobox rather than a dropdown. Name comes
  // first because that is what a host is told at the door; username and email are searchable
  // to tell two people with the same name apart.
  const pingolfPurchaseUserOptions = useMemo(() => {
    return pingolfPurchaseCandidates.map(user => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const displayName = fullName || user.preferred_name || user.username || user.email;
      const preferredSuffix = user.preferred_name && user.preferred_name !== user.first_name
        ? ` "${user.preferred_name}"`
        : '';

      return {
        value: user.id,
        label: `${displayName}${preferredSuffix}${user.email ? ` (${user.email})` : ''}`,
        searchText: `${fullName} ${user.preferred_name || ''} ${user.username || ''} ${user.email || ''}`.toLowerCase()
      };
    });
  }, [pingolfPurchaseCandidates]);

  const selectedPingolfPurchaseUserOption = useMemo(() => {
    return pingolfPurchaseUserOptions.find(option => String(option.value) === String(purchaseTargetUserId)) || null;
  }, [pingolfPurchaseUserOptions, purchaseTargetUserId]);

  const selectedHostUserOption = useMemo(() => {
    return hostUserOptions.find(option => String(option.value) === String(selectedUserId)) || null;
  }, [hostUserOptions, selectedUserId]);

  // Loaded when the purchase modal opens rather than on mount: it is a full member roster and
  // only a host or admin buying on someone's behalf ever needs it.
  useEffect(() => {
    if (showPingolfPurchaseModal && (isAdmin || isHost)) {
      fetchPingolfPurchaseCandidates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPingolfPurchaseModal, isAdmin, isHost, eventId]);

  useEffect(() => {
    fetchEventDetails();
    fetchEventUpdates();
    // Only fetch RSVPs and users if admin or host
    // We'll check after event details are loaded
    if (isAdmin) {
      fetchRSVPs();
      fetchUsers();
    }
    // Always fetch current user's RSVP if logged in
    if (currentUser) {
      fetchMyRsvp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isAdmin]);

  useEffect(() => {
    if (event?.event_metadata?.pinball_format === 'pingolf' && currentUser) {
      fetchPingolfCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, event?.event_metadata?.pinball_format, currentUser?.id]);

  useEffect(() => {
    if (event?.event_metadata?.pinball_format === 'pingolf') {
      fetchPingolfLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, event?.event_metadata?.pinball_format]);

  useEffect(() => {
    if (event?.event_metadata?.pinball_format !== 'pingolf') {
      return undefined;
    }

    setIsPingolfLiveUpdatesPaused(!websocketService.getIsConnected());

    const unsubscribeConnected = websocketService.on('connected', () => {
      setIsPingolfLiveUpdatesPaused(false);
      fetchPingolfLeaderboard();
    });

    const unsubscribeDisconnected = websocketService.on('disconnected', () => {
      setIsPingolfLiveUpdatesPaused(true);
    });

    const unsubscribePingolfUpdate = websocketService.on('pingolf_leaderboard_updated', (payload) => {
      if (!payload?.eventId || Number(payload.eventId) === Number(eventId)) {
        fetchPingolfLeaderboard();
      }
    });

    // Sales opening or closing, and the night being finalized or canceled, all live on the
    // event rather than the board - so this refetches the event, which is what re-renders the
    // Buy Card button and the "why sales are closed" note beside it.
    const unsubscribePingolfEventUpdate = websocketService.on('pingolf_event_updated', (payload) => {
      if (!payload?.eventId || Number(payload.eventId) === Number(eventId)) {
        fetchEventDetails();
      }
    });

    return () => {
      unsubscribeConnected();
      unsubscribeDisconnected();
      unsubscribePingolfUpdate();
      unsubscribePingolfEventUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.event_metadata?.pinball_format, eventId]);

  useEffect(() => {
    if (!pingolfCards.length) {
      setSelectedPingolfCardId(null);
      return;
    }

    if (selectedPingolfCardId && pingolfCards.some(card => card.id === selectedPingolfCardId)) {
      return;
    }

    const preferredCard = pingolfCards.find(card => card.status === 'draft') || pingolfCards[0];
    setSelectedPingolfCardId(preferredCard.id);
  }, [pingolfCards, selectedPingolfCardId]);

  useEffect(() => {
    if (!selectedPingolfCardId) {
      setPingolfDraftHoleInputs({});
      return;
    }

    const selectedCard = pingolfCards.find(card => card.id === selectedPingolfCardId);
    if (!selectedCard) {
      setPingolfDraftHoleInputs({});
      return;
    }

    const nextInputs = {};
    (selectedCard.hole_results || []).forEach((result) => {
      if (result.reached !== true && result.reached !== false) {
        return;
      }

      nextInputs[result.event_hole_id] = result.reached
        ? { reached: true, crossBall: String(result.cross_ball), doubled: Boolean(result.doubled), finalTotal: '' }
        : { reached: false, crossBall: '', doubled: false, finalTotal: String(result.final_total) };
    });

    // This effect re-seeds every input whenever pingolfCards changes - including the refetch
    // that each hole save triggers. Typing into the free-text final-score field debounces a
    // save, so keystrokes made while that save is in flight were being overwritten by the
    // response's older value (type "100", it saves, type another "0", the reply resets the
    // box to "100"). The hole the user currently has open keeps its local value instead.
    setPingolfDraftHoleInputs((prev) => {
      const preservedId = preservedHoleInputIdRef.current;
      if (preservedId !== null && preservedId !== undefined && prev[preservedId]) {
        return { ...nextInputs, [preservedId]: prev[preservedId] };
      }
      return nextInputs;
    });
  }, [selectedPingolfCardId, pingolfCards]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/events/${eventId}`);
      setEvent(response.data);
      setError(null);

      // Check if user is a host of this event after fetching event details
      const eventData = response.data;
      const userIsHost = eventData.hosts && eventData.hosts.some(host => host.id === currentUser?.id);
      setIsHost(userIsHost);

      // If user is a host (but not admin), fetch RSVPs
      if (!isAdmin && userIsHost) {
        fetchRSVPs();
      }
    } catch (err) {
      setError('Failed to load event details');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRsvp = async () => {
    try {
      const response = await apiService.get(`/events/${eventId}/my-rsvp`);
      setMyCurrentRsvp(response.data);
      setMyRsvpData({ status: response.data.status, message: response.data.message || '' });
    } catch (err) {
      // 404 means user doesn't have an RSVP yet - that's okay
      if (err.response && err.response.status === 404) {
        setMyCurrentRsvp(null);
      } else {
        console.error('Error fetching my RSVP:', err);
      }
    }
  };

  const fetchRSVPs = async () => {
    try {
      const response = await apiService.get(`/events/${eventId}/rsvps`);
      setRsvps(response.data);

      // Check if current user has an RSVP
      const userRsvp = response.data.find(r => r.user_id === currentUser?.id);
      setMyCurrentRsvp(userRsvp || null);
      if (userRsvp) {
        setMyRsvpData({ status: userRsvp.status, message: userRsvp.message || '' });
      }
    } catch (err) {
      // If user doesn't have permission (403), silently fail
      // Regular users who RSVP'd don't need to see all RSVPs
      if (err.response && err.response.status === 403) {
        console.log('User does not have permission to view RSVPs');
      } else {
        console.error('Error fetching RSVPs:', err);
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.get('/users?hostEligible=true');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchPingolfPurchaseCandidates = async () => {
    try {
      const response = await apiService.get(`/events/${eventId}/pingolf-purchase-candidates`);
      setPingolfPurchaseCandidates(response.data || []);
    } catch (err) {
      console.error('Error fetching PinGolf purchase candidates:', err);
      setPingolfPurchaseCandidates([]);
    }
  };

  const fetchPingolfCards = async (targetUserId) => {
    try {
      setLoadingPingolfCards(true);
      const response = await apiService.get(`/events/${eventId}/pingolf-cards`, {
        params: targetUserId ? { userId: targetUserId } : undefined
      });
      setPingolfCards(response.data || []);
      setPingolfPurchaseError(null);
    } catch (err) {
      console.error('Error fetching Pingolf cards:', err);
      setPingolfPurchaseError(err.response?.data?.error || 'Failed to load PinGolf cards.');
    } finally {
      setLoadingPingolfCards(false);
    }
  };

  const fetchPingolfLeaderboard = async () => {
    try {
      setLoadingPingolfLeaderboard(true);
      const response = await apiService.get(`/events/${eventId}/pingolf-leaderboard`);
      setPingolfLeaderboard(response.data || []);
      setPingolfLeaderboardError(null);
    } catch (err) {
      console.error('Error fetching Pingolf leaderboard:', err);
      setPingolfLeaderboardError(err.response?.data?.error || 'Failed to load PinGolf leaderboard.');
    } finally {
      setLoadingPingolfLeaderboard(false);
    }
  };

  const handlePurchasePingolfCard = async () => {
    const isOnBehalfPurchase = (isAdmin || isHost) && purchaseTargetUserId;
    const targetUser = isOnBehalfPurchase
      ? pingolfPurchaseCandidates.find(user => String(user.id) === String(purchaseTargetUserId))
      : null;
    const targetName = targetUser
      ? (`${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim() || targetUser.preferred_name || targetUser.username || targetUser.email)
      : null;

    // The "can purchase" flag rendered on the button is derived from `event` at
    // render time and doesn't automatically recompute as time passes (e.g. sales
    // auto-close when the event's time window ends) - if the page has been sitting
    // open, the button can still look enabled after that boundary. Re-check here,
    // and make sure the rejection is actually visible if it does happen.
    if (!deriveCanPurchasePingolfCards(event)) {
      const message = 'PinGolf card sales are closed for this event';
      setPingolfPurchaseError(message);
      showToast(message, 'error');
      return;
    }

    setBuyingPingolfCard(true);
    try {
      const response = await apiService.post(`/events/${eventId}/pingolf-cards/purchase`, {
        userId: isOnBehalfPurchase ? parseInt(purchaseTargetUserId, 10) : undefined,
        useTab: useTabForPingolfPurchase
      });
      const purchasedCard = response?.data?.card || null;

      await fetchPingolfCards();
      await fetchPingolfLeaderboard();
      setPurchaseTargetUserId('');
      setPingolfPurchaseError(null);
      setError(null);
      setShowPingolfPurchaseModal(false);

      // Straight to the card instead of a second "Purchase Complete" dialog - the buyer just
      // confirmed, and the only thing to do with a new card is start filling it in. An
      // on-behalf purchase stays put, since that card belongs to someone else to play.
      if (purchasedCard && !isOnBehalfPurchase) {
        navigate(`/events/${eventId}/pingolf-cards/${purchasedCard.id}`);
      } else {
        // Naming the player matters here: the host is buying at the door for someone else and
        // needs to see the card landed on the right account, not just that something happened.
        showToast(targetName ? `Card purchased for ${targetName}.` : 'Card purchased.', 'success');
      }
    } catch (err) {
      console.error('Error purchasing Pingolf card:', err);
      const message = err.response?.data?.error || 'Failed to purchase PinGolf card.';
      setPingolfPurchaseError(message);
      showToast(message, 'error');
    } finally {
      setBuyingPingolfCard(false);
    }
  };

  const [cosigningPingolfCardId, setCosigningPingolfCardId] = useState(null);
  const [signingOffPingolfCardId, setSigningOffPingolfCardId] = useState(null);

  // E10: a second player co-signs the card during play.
  const handleCosignPingolfCard = async (cardId) => {
    setCosigningPingolfCardId(cardId);
    try {
      await apiService.post(`/events/${eventId}/pingolf-cards/${cardId}/cosign`);
      await fetchPingolfCards();
      setPingolfPurchaseError(null);
    } catch (err) {
      console.error('Error co-signing Pingolf card:', err);
      setPingolfPurchaseError(err.response?.data?.error || 'Failed to co-sign PinGolf card.');
    } finally {
      setCosigningPingolfCardId(null);
    }
  };

  // E10: host/admin sign-off, required before a solo (no co-signer) card can be submitted.
  const handleHostSignOffPingolfCard = async (cardId, note) => {
    setSigningOffPingolfCardId(cardId);
    try {
      await apiService.post(`/events/${eventId}/pingolf-cards/${cardId}/host-sign-off`, { note: note || undefined });
      await fetchPingolfCards();
      setPingolfPurchaseError(null);
    } catch (err) {
      console.error('Error signing off Pingolf card:', err);
      setPingolfPurchaseError(err.response?.data?.error || 'Failed to sign off PinGolf card.');
    } finally {
      setSigningOffPingolfCardId(null);
    }
  };

  const handleAbandonPingolfCard = async () => {
    if (!pingolfAbandonTargetCardId) {
      return;
    }

    setAbandoningPingolfCardId(pingolfAbandonTargetCardId);
    try {
      await apiService.post(`/events/${eventId}/pingolf-cards/${pingolfAbandonTargetCardId}/abandon`);
      await fetchPingolfCards();
      setPingolfPurchaseError(null);
      setPingolfAbandonTargetCardId(null);
    } catch (err) {
      console.error('Error abandoning Pingolf card:', err);
      setPingolfPurchaseError(err.response?.data?.error || 'Failed to abandon PinGolf card.');
    } finally {
      setAbandoningPingolfCardId(null);
    }
  };

  // The player self-reports which ball crossed the target (plus whether the final total also
  // doubled it), or, on a miss, the actual final score. The server validates and computes
  // hole_result server-side (E1) - this never sends a client-computed result.
  const validatePingolfHoleReport = (report, holeNumber) => {
    if (report.reached) {
      const crossBall = parseInt(report.crossBall, 10);
      if (!Number.isInteger(crossBall) || crossBall < 1) {
        setPingolfPurchaseError(`Hole ${holeNumber}: choose which ball reached the target.`);
        return null;
      }
      return { reached: true, crossBall, doubled: Boolean(report.doubled) };
    }

    const finalTotal = parseInt(report.finalTotal, 10);
    if (!Number.isInteger(finalTotal) || finalTotal < 0) {
      setPingolfPurchaseError(`Hole ${holeNumber}: enter the final score.`);
      return null;
    }
    return { reached: false, finalTotal };
  };

  const handleSavePingolfHoleResult = async (cardId, eventHoleId, holeNumber, report) => {
    const payload = validatePingolfHoleReport(report, holeNumber);
    if (!payload) {
      return;
    }

    setSavingPingolfResultHoleId(eventHoleId);
    try {
      await apiService.put(`/events/${eventId}/pingolf-cards/${cardId}/hole-results/${eventHoleId}`, payload);
      await fetchPingolfCards();
      setPingolfHoleSaveError(null);
    } catch (err) {
      console.error('Error saving Pingolf hole result:', err);
      // Reported on the hole modal, not the page - the user is inside the modal when this
      // fires and needs the message next to the field they have to correct.
      setPingolfHoleSaveError(err.response?.data?.error || 'Failed to save hole result.');
    } finally {
      setSavingPingolfResultHoleId(null);
    }
  };

  // Deselecting in the entry modal has to actually erase the saved result. Without this the
  // draft went back to "nothing selected" locally while the server kept the score the player
  // had already auto-saved, so the card list still showed the value they thought they cleared.
  const handleClearPingolfHoleResult = async (cardId, eventHoleId) => {
    setSavingPingolfResultHoleId(eventHoleId);
    try {
      await apiService.clearPingolfHoleResult(eventId, cardId, eventHoleId);
      await fetchPingolfCards();
      setPingolfHoleSaveError(null);
    } catch (err) {
      console.error('Error clearing Pingolf hole result:', err);
      setPingolfHoleSaveError(err.response?.data?.error || 'Failed to clear hole result.');
    } finally {
      setSavingPingolfResultHoleId(null);
    }
  };

  const persistPendingPingolfHoleInputs = async (card) => {
    if (!card || !Array.isArray(event?.pingolf_holes)) {
      return;
    }

    const currentResults = new Map((card.hole_results || []).map(result => [result.event_hole_id, result]));

    for (const hole of event.pingolf_holes) {
      const draft = pingolfDraftHoleInputs[hole.id];
      if (!draft || draft.reached === null || draft.reached === undefined) {
        continue;
      }

      const payload = validatePingolfHoleReport(draft, hole.hole_number);
      if (!payload) {
        throw new Error('invalid_hole_score');
      }

      const existing = currentResults.get(hole.id);
      const existingPayload = existing && existing.reached !== null && existing.reached !== undefined
        ? (existing.reached
          ? { reached: true, crossBall: existing.cross_ball, doubled: Boolean(existing.doubled) }
          : { reached: false, finalTotal: existing.final_total })
        : null;
      if (existingPayload && JSON.stringify(existingPayload) === JSON.stringify(payload)) {
        continue;
      }

      await apiService.put(`/events/${eventId}/pingolf-cards/${card.id}/hole-results/${hole.id}`, payload);
    }
  };

  const handleSubmitPingolfCard = async (cardId) => {
    setSubmittingPingolfCard(true);
    try {
      const cardToSubmit = pingolfCards.find(card => card.id === cardId);
      await persistPendingPingolfHoleInputs(cardToSubmit);
      await apiService.post(`/events/${eventId}/pingolf-cards/${cardId}/submit`);
      await fetchPingolfCards();
      await fetchPingolfLeaderboard();
      setPingolfPurchaseError(null);
      setShowPingolfSubmitConfirmModal(false);
      // A toast, not a second modal - the user already confirmed on one, and stacking a
      // success modal on top makes them dismiss two dialogs to finish one action.
      showToast('Card submitted - your score is on the leaderboard.', 'success');
    } catch (err) {
      console.error('Error submitting Pingolf card:', err);
      setPingolfPurchaseError(err.response?.data?.error || 'Failed to submit PinGolf card.');
    } finally {
      setSubmittingPingolfCard(false);
    }
  };

  const handleReopenPingolfCard = async () => {
    if (!pingolfReopenTargetCardId) {
      return;
    }

    setReopeningPingolfCardId(pingolfReopenTargetCardId);
    try {
      await apiService.post(`/events/${eventId}/pingolf-cards/${pingolfReopenTargetCardId}/reopen`, {
        reason: pingolfReopenReason.trim() || undefined
      });
      await fetchPingolfCards();
      setPingolfPurchaseError(null);
      setPingolfReopenTargetCardId(null);
      setPingolfReopenReason('');
    } catch (err) {
      console.error('Error reopening Pingolf card:', err);
      setPingolfPurchaseError(err.response?.data?.error || 'Failed to reopen PinGolf card.');
    } finally {
      setReopeningPingolfCardId(null);
    }
  };

  const handleCorrectPingolfHoleResult = async () => {
    if (!pingolfCorrectionDraft.cardId || !pingolfCorrectionDraft.holeId) {
      return;
    }

    const payload = validatePingolfHoleReport(pingolfCorrectionDraft, pingolfCorrectionDraft.holeId);
    if (!payload) {
      return;
    }

    setCorrectingPingolfHoleId(pingolfCorrectionDraft.holeId);
    try {
      await apiService.put(`/events/${eventId}/pingolf-cards/${pingolfCorrectionDraft.cardId}/hole-results/${pingolfCorrectionDraft.holeId}/correct`, {
        ...payload,
        reason: pingolfCorrectionDraft.reason.trim() || undefined
      });
      await fetchPingolfCards();
      setPingolfPurchaseError(null);
      setPingolfCorrectionDraft({
        cardId: null,
        holeId: null,
        existingReport: null,
        reached: null,
        crossBall: '',
        doubled: false,
        finalTotal: '',
        reason: ''
      });
    } catch (err) {
      console.error('Error correcting Pingolf hole result:', err);
      setPingolfPurchaseError(err.response?.data?.error || 'Failed to correct PinGolf hole result.');
    } finally {
      setCorrectingPingolfHoleId(null);
    }
  };

  const fetchEventUpdates = async () => {
    try {
      const response = await apiService.get(`/events/${eventId}/updates`);
      setEventUpdates(response.data);
    } catch (err) {
      console.error('Error fetching event updates:', err);
    }
  };

  const handleCheckIn = async (userId) => {
    try {
      await apiService.post(`/events/${eventId}/checkin`, { userId });
      fetchRSVPs();
      setError(null);
    } catch (err) {
      console.error('Error checking in user:', err);
      setError('Failed to check in user. Please try again.');
    }
  };

  const handleUndoCheckIn = (userId) => {
    setConfirmDialog({
      title: 'Undo Check-In?',
      message: 'Undo this check-in?',
      confirmLabel: 'Undo Check-In',
      danger: false,
      onConfirm: async () => {
        try {
          setConfirmDialogSubmitting(true);
          await apiService.post(`/events/${eventId}/undo-checkin`, { userId });
          fetchRSVPs();
          setError(null);
          setConfirmDialog(null);
        } catch (err) {
          console.error('Error undoing check-in:', err);
          setError('Failed to undo check-in. Please try again.');
          setConfirmDialog(null);
        } finally {
          setConfirmDialogSubmitting(false);
        }
      }
    });
  };

  const handleDeleteRsvp = (userId, userName) => {
    setConfirmDialog({
      title: 'Delete RSVP?',
      message: `Delete RSVP for ${userName}? This cannot be undone.`,
      confirmLabel: 'Delete RSVP',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmDialogSubmitting(true);
          await apiService.delete(`/events/${eventId}/rsvp/${userId}`);
          fetchRSVPs();
          setError(null);
          setConfirmDialog(null);
        } catch (err) {
          console.error('Error deleting RSVP:', err);
          setError('Failed to delete RSVP. Please try again.');
          setConfirmDialog(null);
        } finally {
          setConfirmDialogSubmitting(false);
        }
      }
    });
  };

  const copyPaymentLink = (paymentLink) => {
    navigator.clipboard.writeText(paymentLink.url).then(() => {
      console.log(`${paymentLink.title} copied to clipboard`);
    }).catch(err => {
      console.error('Failed to copy payment link:', err);
      setError('Failed to copy payment link. Please copy manually.');
    });
  };

  const showPaymentQr = (paymentLink) => {
    setActivePaymentQr(paymentLink);
  };

  const closePaymentQr = () => {
    setActivePaymentQr(null);
  };

  const toggleHostBio = (hostId) => {
    setExpandedHostBios(prev => ({
      ...prev,
      [hostId]: !prev[hostId]
    }));
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Takes the id directly when a caller has one to hand. Reading it back off state means
  // a picker that selects and assigns in one action assigns whoever was selected last.
  const handleAssignHost = async (userIdArg) => {
    const userId = userIdArg ?? selectedUserId;

    if (!userId) {
      setError('Please select a user');
      return;
    }

    try {
      await apiService.post(`/events/${eventId}/assign-host`, {
        userId: parseInt(userId)
      });
      setShowHostAssignment(false);
      setSelectedUserId('');
      fetchEventDetails();
      setError(null);
    } catch (err) {
      console.error('Error assigning host:', err);
      setError('Failed to assign host. Please try again.');
    }
  };

  const handleRemoveHost = (userId) => {
    setConfirmDialog({
      title: 'Remove Host?',
      message: 'Remove this host?',
      confirmLabel: 'Remove Host',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmDialogSubmitting(true);
          await apiService.delete(`/events/${eventId}/remove-host/${userId}`);
          fetchEventDetails();
          setError(null);
          setConfirmDialog(null);
        } catch (err) {
          console.error('Error removing host:', err);
          setError('Failed to remove host. Please try again.');
          setConfirmDialog(null);
        } finally {
          setConfirmDialogSubmitting(false);
        }
      }
    });
  };

  // Called both as a form submit handler and straight from a sheet's Add button, so the
  // event is optional. It used to assume one and threw on the button path.
  const handleWalkinSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    try {
      await apiService.post(`/events/${eventId}/walkin`, walkinData);
      setShowWalkinForm(false);
      setWalkinData({ firstName: '', lastName: '', email: '', phone: '' });
      fetchRSVPs();
      setError(null);
      return true;
    } catch (err) {
      console.error('Error adding walk-in:', err);
      // Say what the server actually objected to. A flat "please try again" sent the host
      // round the same failing loop, since the thing it wanted was a valid email.
      setError(parseErrorMessage(err, 'Could not add that walk-in'));
      return false;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) {
      return 'TBD';
    }

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTimeRange = (dateString, durationMinutes) => {
    if (!dateString) {
      return 'Time TBD';
    }

    const startDate = new Date(dateString);
    const safeDurationMinutes = Number(durationMinutes) || 240;
    const endDate = new Date(startDate.getTime() + safeDurationMinutes * 60 * 1000);
    const timeFormat = {
      hour: 'numeric',
      minute: '2-digit'
    };

    return `${startDate.toLocaleTimeString('en-US', timeFormat)} - ${endDate.toLocaleTimeString('en-US', timeFormat)}`;
  };

  const handleOpenPingolfCardFromHeader = async () => {
    const draftCard = pingolfCards.find(card => card.status === 'draft' && card.user_id === currentUser?.id);
    if (draftCard) {
      navigate(`/events/${eventId}/pingolf-cards/${draftCard.id}`);
      return;
    }

    // Sales-open state is time-window-derived and doesn't recompute on its own as
    // the clock ticks (only on a re-render) - refresh the event right before
    // deciding whether to show the purchase flow, so a page left open past the
    // sales window doesn't offer a purchase that's actually already closed.
    await fetchEventDetails();

    // Refreshed per open rather than once on mount: an admin can flip a member's tab access
    // while the page is sitting open, and this is the only moment the answer is used.
    try {
      const meResponse = await apiService.get('/users/me');
      setCurrentUserTabEnabled(Boolean(meResponse.data?.tab_enabled));
    } catch (err) {
      console.error('Failed to load tab eligibility:', err);
      setCurrentUserTabEnabled(false);
    }

    setShowPingolfPurchaseModal(true);
    setPingolfPurchaseError(null);
  };

  const handleJumpToPingolfHostControls = () => {
    navigate(`/events/${eventId}/host-controls`);
  };

  const handleEditEvent = () => {
    // Convert UTC date to local timezone for datetime-local input
    let localDateTimeString = '';
    if (event.event_date) {
      const utcDate = new Date(event.event_date);
      // Get timezone offset and adjust
      const tzOffset = utcDate.getTimezoneOffset() * 60000;
      const localDate = new Date(utcDate.getTime() - tzOffset);
      localDateTimeString = localDate.toISOString().slice(0, 16);
    }

    setEditEventData({
      title: event.title,
      description: event.description || '',
      event_date: localDateTimeString,
      end_time: endTimeInputValue(localDateTimeString, event.duration_minutes),
      location: event.location || '',
      event_category: event.event_category || 'other',
      event_metadata: event.event_metadata || {},
      // The website page this event syncs to. Admins can point it at an existing page.
      wordpress_link: event.wordpress_url || '',
      is_private: Boolean(event.is_private)
    });
    setIsEditingEvent(true);
  };

  const handleCancelEditEvent = () => {
    setIsEditingEvent(false);
    setEditEventData({
      title: '',
      description: '',
      event_date: '',
      location: '',
      event_category: 'other',
      event_metadata: {}
    });
  };

  const handleAssignGame = () => {
    const metadata = event.event_metadata || {};
    setFeaturedGameData({
      game_name: metadata.game_name || '',
      game_description: metadata.game_mechanics_description || ''
    });
    setShowFeaturedGameForm(true);
  };

  const handleCancelFeaturedGame = () => {
    setShowFeaturedGameForm(false);
    setFeaturedGameData({
      game_name: '',
      game_description: ''
    });
  };

  const handleSaveFeaturedGame = async () => {
    try {
      const updatedMetadata = {
        ...(event.event_metadata || {}),
        game_name: featuredGameData.game_name,
        game_mechanics_description: featuredGameData.game_description
      };

      await apiService.put(`/events/${eventId}`, {
        event_metadata: updatedMetadata
      });

      setShowFeaturedGameForm(false);
      fetchEventDetails();
    } catch (err) {
      console.error('Error updating featured game:', err);
      setError(`Failed to update featured ${event.event_category === 'book_club' ? 'book' : 'game'}. Please try again.`);
    }
  };

  const handleRemoveFeaturedGame = () => {
    const itemLabel = event.event_category === 'book_club' ? 'book' : 'game';
    setConfirmDialog({
      title: `Remove Featured ${itemLabel === 'book' ? 'Book' : 'Game'}?`,
      message: `Remove the featured ${itemLabel} assignment?`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmDialogSubmitting(true);
          const updatedMetadata = {
            ...(event.event_metadata || {}),
            game_name: '',
            game_mechanics_description: ''
          };

          await apiService.put(`/events/${eventId}`, {
            event_metadata: updatedMetadata
          });

          fetchEventDetails();
          setConfirmDialog(null);
        } catch (err) {
          console.error('Error removing featured game:', err);
          setError(`Failed to remove featured ${itemLabel}. Please try again.`);
          setConfirmDialog(null);
        } finally {
          setConfirmDialogSubmitting(false);
        }
      }
    });
  };

  const handleSaveEvent = async () => {
    try {
      // Convert local datetime to UTC for storage
      const localDate = new Date(editEventData.event_date);
      const tzOffset = localDate.getTimezoneOffset() * 60000;
      const utcDate = new Date(localDate.getTime() - tzOffset);

      const { wordpress_link: wordpressLink, is_private: isPrivate, end_time: endTime, ...rest } = editEventData;
      const eventDataToSave = {
        ...rest,
        event_date: utcDate.toISOString()
      };
      const durationMinutes = durationFromEndTime(editEventData.event_date, endTime);
      if (durationMinutes) eventDataToSave.duration_minutes = durationMinutes;
      // Admin-only on the server too; a host's edit leaves it alone.
      if (isAdmin) eventDataToSave.is_private = Boolean(isPrivate);
      // Only sent when changed: the server looks the address up on the website, and an
      // ordinary edit should not depend on the website answering.
      const link = (wordpressLink || '').trim();
      if (isAdmin && link !== (event.wordpress_url || '')) {
        eventDataToSave.wordpress_link = link;
      }

      await apiService.put(`/events/${eventId}`, eventDataToSave);
      setIsEditingEvent(false);
      fetchEventDetails();
      // Success - no alert needed, form closes automatically
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err.response?.data?.error || 'Failed to update event. Please try again.');
    }
  };

  // The photo saves on its own (EventPhotoControl), so patch it in rather than refetch:
  // a refetch shows the loading screen and would close the edit form under the user.
  const handleEventPhotoChange = (photoUrl) => {
    setEvent((current) => (current ? { ...current, photo_url: photoUrl } : current));
  };

  const handleDeleteEvent = () => {
    setConfirmDialog({
      title: 'Delete Event?',
      message: 'Are you sure you want to delete this event? This action cannot be undone.',
      confirmLabel: 'Delete Event',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmDialogSubmitting(true);
          await apiService.delete(`/events/${eventId}`);
          navigate('/events');
        } catch (err) {
          console.error('Error deleting event:', err);
          setError('Failed to delete event. Please try again.');
          setConfirmDialog(null);
        } finally {
          setConfirmDialogSubmitting(false);
        }
      }
    });
  };

  // Real self-removal. Setting the status to "Not going" still leaves you on the list,
  // saying no; this takes your name off entirely, which previously needed a host.
  const handleRemoveMyRsvp = () => {
    setConfirmDialog({
      title: 'Remove your RSVP?',
      message: 'You come off the list for this event. You can RSVP again any time.',
      confirmLabel: 'Remove my RSVP',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmDialogSubmitting(true);
          await apiService.delete(`/events/${eventId}/rsvp/${currentUser.id}`);
          setMyCurrentRsvp(null);
          setShowMyRsvpForm(false);
          fetchRSVPs();
          setError(null);
          setConfirmDialog(null);
        } catch (err) {
          console.error('Error removing RSVP:', err);
          setError('Could not remove your RSVP. Please try again.');
          setConfirmDialog(null);
        } finally {
          setConfirmDialogSubmitting(false);
        }
      }
    });
  };

  // Saves a status and an optional note in one call. The member view sets a status with
  // one tap and edits the note separately, so neither can be made to wait on the other
  // being filled in first.
  const saveMyRsvp = async ({ status, message }) => {
    const next = {
      status: status ?? myCurrentRsvp?.status ?? 'going',
      message: message ?? myCurrentRsvp?.message ?? ''
    };

    try {
      await apiService.put(`/events/${eventId}/rsvp`, next);
      fetchMyRsvp();
      if (isAdmin || isHost) {
        fetchRSVPs();
      }
      setError(null);
    } catch (err) {
      console.error('Error saving RSVP:', err);
      setError('Could not save your RSVP. Please try again.');
    }
  };

  const handleMyRsvp = async () => {
    try {
      await apiService.put(`/events/${eventId}/rsvp`, myRsvpData);
      setShowMyRsvpForm(false);
      fetchMyRsvp(); // Refresh user's RSVP status
      // If admin/host, also refresh the full RSVPs list
      if (isAdmin || isHost) {
        fetchRSVPs();
      }
    } catch (err) {
      console.error('Error updating RSVP:', err);
      setError('Failed to update RSVP. Please try again.');
    }
  };

  // Same as the walk-in handler: wired to a plain button on the manage screen as well as
  // to a form, so the event is optional.
  const handleSendUpdate = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (!updateMessage.trim()) {
      setError('Please enter an update message');
      return;
    }

    setSendingUpdate(true);
    try {
      const response = await apiService.post(`/events/${eventId}/send-update`, {
        message: updateMessage
      });

      setShowUpdateForm(false);
      setUpdateMessage('');
      setError(null);
      fetchEventUpdates(); // Refresh the updates list

      // Show success message
      showToast(`Update sent to ${response.data.successCount} attendee(s)`, 'success');
    } catch (err) {
      console.error('Error sending update:', err);
      setError(err.response?.data?.error || 'Failed to send update. Please try again.');
    } finally {
      setSendingUpdate(false);
    }
  };

  return {
    event, rsvps, users, loading, isHost, error, setError,
    showHostAssignment, setShowHostAssignment,
    selectedUserId, setSelectedUserId,
    showWalkinForm, setShowWalkinForm,
    walkinData, setWalkinData,
    isEditingEvent, setIsEditingEvent,
    editEventData, setEditEventData,
    showUpdateForm, setShowUpdateForm,
    updateMessage, setUpdateMessage,
    sendingUpdate,
    showMyRsvpForm, setShowMyRsvpForm,
    myRsvpData, setMyRsvpData,
    myCurrentRsvp,
    eventUpdates,
    showFeaturedGameForm, setShowFeaturedGameForm,
    featuredGameData, setFeaturedGameData,
    showRsvpActionsMenu, setShowRsvpActionsMenu,
    expandedHostBios,
    expandedDescription, setExpandedDescription,
    activePaymentQr,
    pingolfCards,
    loadingPingolfCards,
    buyingPingolfCard,
    abandoningPingolfCardId,
    purchaseTargetUserId, setPurchaseTargetUserId,
    pingolfPurchaseCandidates,
    pingolfPurchaseUserOptions,
    selectedPingolfPurchaseUserOption,
    useTabForPingolfPurchase, setUseTabForPingolfPurchase,
    currentUserTabEnabled,
    pingolfPurchaseError, setPingolfPurchaseError,
    selectedPingolfCardId, setSelectedPingolfCardId,
    savingPingolfResultHoleId,
    submittingPingolfCard,
    pingolfDraftHoleInputs, setPingolfDraftHoleInputs,
    pingolfHoleSaveError, setPingolfHoleSaveError,
    setPreservedPingolfHoleInputId,
    reopeningPingolfCardId,
    correctingPingolfHoleId,
    pingolfLeaderboard,
    loadingPingolfLeaderboard,
    pingolfLeaderboardError,
    pingolfLeaderboardDetailRow, setPingolfLeaderboardDetailRow,
    isPingolfLiveUpdatesPaused,
    showPingolfPurchaseModal, setShowPingolfPurchaseModal,
    showPingolfSubmitConfirmModal, setShowPingolfSubmitConfirmModal,
    pingolfAbandonTargetCardId, setPingolfAbandonTargetCardId,
    pingolfReopenTargetCardId, setPingolfReopenTargetCardId,
    pingolfReopenReason, setPingolfReopenReason,
    pingolfCorrectionDraft, setPingolfCorrectionDraft,
    confirmDialog, setConfirmDialog,
    confirmDialogSubmitting,
    hostUserOptions,
    selectedHostUserOption,

    fetchEventDetails,
    fetchMyRsvp,
    fetchRSVPs,
    fetchUsers,
    fetchPingolfCards,
    fetchPingolfLeaderboard,
    handlePurchasePingolfCard,
    handleAbandonPingolfCard,
    handleSavePingolfHoleResult,
    handleClearPingolfHoleResult,
    handleSubmitPingolfCard,
    handleCosignPingolfCard, cosigningPingolfCardId,
    handleHostSignOffPingolfCard, signingOffPingolfCardId,
    handleReopenPingolfCard,
    handleCorrectPingolfHoleResult,
    fetchEventUpdates,
    handleCheckIn,
    handleUndoCheckIn,
    handleDeleteRsvp,
    copyPaymentLink,
    showPaymentQr,
    closePaymentQr,
    toggleHostBio,
    truncateText,
    handleAssignHost,
    handleRemoveHost,
    handleWalkinSubmit,
    formatDate,
    formatShortDate,
    formatTimeRange,
    handleOpenPingolfCardFromHeader,
    handleJumpToPingolfHostControls,
    handleEditEvent,
    handleCancelEditEvent,
    handleAssignGame,
    handleCancelFeaturedGame,
    handleSaveFeaturedGame,
    handleRemoveFeaturedGame,
    handleSaveEvent,
    handleEventPhotoChange,
    handleDeleteEvent,
    handleMyRsvp,
    saveMyRsvp,
    handleRemoveMyRsvp,
    handleSendUpdate
  };
}
